import type { MessageInfoAttachment } from '../../../api/cmail/cmail.dto';
import useNotifications from '../../../_lib/notifications/useNotifications';
import {
  buildPreviewDocument,
  fetchAttachmentBlobUrl,
  triggerDownload,
} from '../utils/attachmentPreviewUtils';

export const attachmentFileName = (attachment: MessageInfoAttachment): string =>
  attachment.docName || 'download';

export const attachmentUrl = (
  attachment: MessageInfoAttachment
): string | undefined => attachment.signedS3Url || attachment.s3Path;

/** Report an attachment failure as a notification naming the file. */
export const useAttachmentErrorNotifier = (
  attachment: MessageInfoAttachment
) => {
  const notifications = useNotifications();

  // A silent console.error here left the chip looking inert; say what broke.
  return (error: unknown) =>
    notifications.error(
      `Could not open ${attachmentFileName(attachment)}`,
      error instanceof Error ? error.message : undefined
    );
};

/** Whether opening this attachment previews it rather than downloading it. */
export const attachmentPreviews = (
  attachment: MessageInfoAttachment
): boolean => attachment.docContentType === 'application/pdf';

const fetchAttachmentFile = (attachment: MessageInfoAttachment) =>
  fetchAttachmentBlobUrl(
    attachmentUrl(attachment),
    attachment.docContentType || 'application/octet-stream'
  );

/** Save an attachment under its own name, whatever its type. */
export const useAttachmentDownload = (attachment: MessageInfoAttachment) => {
  const notifyError = useAttachmentErrorNotifier(attachment);

  return async () => {
    try {
      const fileUrl = await fetchAttachmentFile(attachment);
      triggerDownload(fileUrl, attachmentFileName(attachment));
    } catch (error) {
      notifyError(error);
    }
  };
};

/**
 * Open an attachment the way its type allows: PDFs preview in a new tab,
 * everything else downloads under its own name.
 */
export const useAttachmentOpen = (attachment: MessageInfoAttachment) => {
  const notifyError = useAttachmentErrorNotifier(attachment);
  const fileName = attachmentFileName(attachment);

  const openPreview = (fileUrl: string) => {
    const html = buildPreviewDocument({ fileUrl, fileName });
    const htmlUrl = URL.createObjectURL(
      new Blob([html], { type: 'text/html;charset=utf-8' })
    );

    // The preview document owns both URLs from here and revokes them when it
    // unloads. Revoking on a timer leaves the page readable but kills every
    // download route out of it, which is the bug this replaced.
    if (!window.open(htmlUrl, '_blank')) {
      URL.revokeObjectURL(htmlUrl);
      URL.revokeObjectURL(fileUrl);
      throw new Error('Your browser blocked the preview window.');
    }
  };

  return async () => {
    try {
      const fileUrl = await fetchAttachmentFile(attachment);

      if (attachmentPreviews(attachment)) {
        openPreview(fileUrl);
        return;
      }

      triggerDownload(fileUrl, fileName);
    } catch (error) {
      notifyError(error);
    }
  };
};
